// Auto-generated style — Stuck Pipe & Fishing formulas.
// All math deterministic. Engineering judgment + company procedures required for action.
import { round, num, positive, comingSoonFn } from "./_common";
// eslint-disable-next-line no-unused-vars
const _imports_ok = { round, num, positive, comingSoonFn };

// =========================================================================
// STUCK PIPE & FISHING
// =========================================================================

// Free-point estimator from stretch test.
// L_free (ft) ≈ (ΔL_in × W_lb/ft × 735,294) / F_lbf
// derived from Hooke's law for a uniform steel column with E = 30×10⁶ psi
// (constant 735,294 = E × area_per_lbft conversion grouped). This is a planning
// estimate; an actual free-point pickup test in the field is the only definitive method.
export function freePointFromStretch({ stretchInches, pipeWeightLbFt, pullForceLbf }) {
  const dL = positive(num(stretchInches, "Stretch"), "Stretch");
  const W = positive(num(pipeWeightLbFt, "Pipe Weight"), "Pipe Weight");
  const F = positive(num(pullForceLbf, "Pull Force"), "Pull Force");
  const L = (dL * W * 735294) / F;
  return {
    result: round(L, 0),
    unit: "ft (estimated free point)",
    formula: "L_free ≈ (ΔL × W × 735,294) / F   (steel pipe, E ≈ 30×10⁶ psi)",
    steps: [
      `ΔL = ${dL} in,  W = ${W} lb/ft,  F = ${F} lbf`,
      `L_free = (${dL} × ${W} × 735,294) / ${F}`,
      `L_free = ${round(L, 0)} ft`,
    ],
    inputs: { "Stretch (in)": dL, "Pipe Weight (lb/ft)": W, "Pull Force (lbf)": F },
    extras: { "Stretch (in)": dL, "Pull (lbf)": F },
    worksheetWarning: true,
  };
}

// Pipe-light / pipe-heavy comparison: hookload vs buoyed string weight.
export function pipeLightHeavy({ hookload, stringAirWeight, mw }) {
  const H = positive(num(hookload, "Hookload"), "Hookload");
  const Wa = positive(num(stringAirWeight, "String Air Weight"), "String Air Weight");
  const M = positive(num(mw, "Mud Weight"), "Mud Weight");
  const BF = 1 - M / 65.5;
  const Wb = Wa * BF;
  const delta = H - Wb;
  const status = delta > 5000 ? "PIPE HEAVY (hookload exceeds buoyed string weight)" :
                 delta < -5000 ? "PIPE LIGHT (hookload below buoyed string weight)" :
                 "Approximately balanced (within ±5,000 lbf)";
  return {
    result: round(delta, 0),
    unit: "lbf (Hookload − Buoyed)",
    formula: "BF = 1 − MW/65.5 ;  Wb = Wair × BF ;  Δ = Hookload − Wb",
    steps: [
      `BF = 1 − ${M}/65.5 = ${round(BF, 4)}`,
      `Buoyed string weight = ${Wa} × ${round(BF, 4)} = ${round(Wb, 0)} lbf`,
      `Δ = ${H} − ${round(Wb, 0)} = ${round(delta, 0)} lbf`,
      `Indication: ${status}`,
    ],
    inputs: { "Hookload (lbf)": H, "String Air Weight (lbf)": Wa, "MW (ppg)": M },
    extras: { "BF": round(BF, 4), "Buoyed Weight (lbf)": round(Wb, 0), "Status": status },
  };
}

// Maximum safe pull margin = published tensile rating × design factor − current hookload
export function maxSafePullMargin({ tensileRatingLbf, designFactor, currentHookload }) {
  const T = positive(num(tensileRatingLbf, "Tensile Rating"), "Tensile Rating");
  const DF = positive(num(designFactor, "Design Factor"), "Design Factor");
  if (DF > 1) throw new Error("Design factor should be ≤ 1 (e.g. 0.8 = 80% of rating)");
  const H = positive(num(currentHookload, "Current Hookload"), "Current Hookload");
  const safe = T * DF;
  const margin = safe - H;
  return {
    result: round(margin, 0),
    unit: "lbf (margin to safe pull)",
    formula: "Safe Pull = Tensile Rating × Design Factor ;  Margin = Safe Pull − Hookload",
    steps: [
      `Safe Pull = ${T} × ${DF} = ${round(safe, 0)} lbf`,
      `Margin = ${round(safe, 0)} − ${H} = ${round(margin, 0)} lbf`,
      margin <= 0 ? "WARNING: current hookload is at or above safe pull limit" : "",
    ].filter(Boolean),
    inputs: { "Tensile Rating (lbf)": T, "Design Factor": DF, "Hookload (lbf)": H },
    extras: { "Safe Pull (lbf)": round(safe, 0) },
    worksheetWarning: true,
  };
}

// Jar Impact — structured support worksheet.
// Real jar impact forces depend on jar design (mechanical vs hydraulic time-delay),
// preload, fluid drag, string elasticity, friction, BHA configuration, hole angle,
// and manufacturer test data. This worksheet packages the run-in conditions for
// engineering review; it does NOT compute a verified impact load. The numeric
// "result" is the overpull entered by the operator (carried through for record).
export function jarImpactWorksheet({ jarType, overpull, stringWeight, stretch, depth, remarks }) {
  const type = (jarType || "mechanical").toLowerCase();
  const OP = positive(num(overpull, "Overpull"), "Overpull");
  const SW = positive(num(stringWeight, "String Weight"), "String Weight");
  const dL = positive(num(stretch, "Stretch"), "Stretch");
  const D  = positive(num(depth, "Depth"), "Depth");
  const note = (typeof remarks === "string" ? remarks.trim() : "") || "—";
  // Stored elastic energy in the string (informational only).
  // U = ½ · F · ΔL   (F in lbf, ΔL in ft → ft-lbf).
  const dLft = dL / 12;
  const storedEnergy = 0.5 * OP * dLft;
  return {
    result: round(OP, 0),
    unit: "lbf (overpull carried through)",
    formula: "Jar Impact Support Worksheet — no impact-force formula applied",
    steps: [
      `Jar type: ${type}`,
      `Overpull at surface: ${OP} lbf`,
      `String weight (buoyed): ${SW} lbf`,
      `Stretch above neutral: ${dL} in (${round(dLft, 3)} ft)`,
      `Depth: ${D} ft`,
      `Stored elastic energy (½·F·ΔL): ${round(storedEnergy, 0)} ft-lbf — informational only`,
      `Remarks: ${note}`,
    ],
    inputs: {
      "Jar Type": type,
      "Overpull (lbf)": OP,
      "String Weight (lbf)": SW,
      "Stretch (in)": dL,
      "Depth (ft)": D,
      "Remarks": note,
    },
    extras: {
      "Stored elastic energy (ft-lbf)": round(storedEnergy, 0),
      "Status": "Advanced / Requires Verification — actual jar impact depends on jar design, fluid drag, string elasticity, friction, BHA configuration, hole angle, and manufacturer data.",
    },
    worksheetWarning: true,
  };
}
// Catalog alias for backwards compatibility
export const jarImpact = jarImpactWorksheet;
